/* Volatile - gift card codes and the charge breakdown.
 *
 * Shopify's Online Store has no AJAX endpoint for applying a discount, so the
 * two moves the platform does support are used directly:
 *
 *   apply   GET  /discount/<code>?redirect=/cart   sets the cart's discount
 *   remove  POST /cart/update.js  {"discount": ""} clears it
 *
 * Both were verified against the live store before this file was written.
 * /cart.js then reports `discount_codes`, `total_discount` and `total_price`,
 * which is everything the breakdown needs.
 *
 * Deliberately a separate file from cart.js: this owns only the elements it
 * introduces ([data-giftcard*], [data-cart-total]) and never touches the line
 * items or the subtotal, which remain cart.js's. The two meet at one place, a
 * MutationObserver on [data-cart-subtotal]: when cart.js rewrites the subtotal
 * after a quantity change, this refetches and re-renders the totals. That keeps
 * cart.js unedited, which matters because it is part of the Razorpay Magic
 * Checkout surface.
 *
 * The word is "gift card" throughout, never "coupon", and no surface lists the
 * codes that exist. A person types the one they were given. The code is a
 * code: upper-cased on the way in and wherever it is echoed back.
 */
(() => {
  if (window.VolatileGiftCardBound) return;
  window.VolatileGiftCardBound = true;
  if (!document.querySelector('[data-giftcard]')) return;

  const fmt = (cents) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(cents / 100));
  const money = (cents) => '₹' + fmt(cents);
  // Mirrors snippets/v-price.liquid so the same .v-price* CSS applies.
  const priceHtml = (cents) =>
    `<span class="v-price" data-price-cents="${cents}">` +
      `<span class="v-price__current">` +
        `<span class="v-price__cur">₹</span>` +
        `<span class="v-price__num">${fmt(cents)}</span>` +
      `</span>` +
    `</span>`;
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const all = (sel) => Array.from(document.querySelectorAll(sel));
  const t = (key, fallback) => {
    const el = document.querySelector('[data-giftcard-strings]');
    if (!el) return fallback;
    try { return (JSON.parse(el.textContent) || {})[key] || fallback; } catch (e) { return fallback; }
  };

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // /cart.js does not return compare_at_price, so the MRP cannot come from the
  // API. Liquid stamps the per-line figure on the row as data-unit-mrp and this
  // multiplies it by the live quantity, which keeps it right after a +/- tap
  // without a reload. Scoped to the [data-cart] the totals live in, because on
  // /cart the page cart and the drawer are both in the document and summing
  // across both would double every number.
  function mrpWithin(root) {
    if (!root) return 0;
    let total = 0;
    root.querySelectorAll('[data-line]').forEach((row) => {
      if (row.hidden || row.classList.contains('is-removing') || row.style.display === 'none') return;
      const qtyEl = row.querySelector('[data-cart-qty]');
      const qty = Number(qtyEl && qtyEl.value) || 0;
      total += (Number(row.dataset.unitMrp) || 0) * qty;
    });
    return total;
  }

  const appliedCode = (cart) => {
    const codes = (cart.discount_codes || []).filter((c) => c.applicable);
    return codes.length ? String(codes[0].code).toUpperCase() : '';
  };

  /* Total MRP, Total savings and Subtotal are always on the sheet. They were
     hidden when the saving was nil; now every variant carries an MRP, so the
     rows always have something to say, and a row that comes and goes read as
     a glitch. */
  function render(cart) {
    const applied = appliedCode(cart);
    const off = cart.total_discount || 0;

    all('[data-cart-total]').forEach((el) => { el.innerHTML = priceHtml(cart.total_price); });

    all('[data-totals]').forEach((totals) => {
      const mrp = mrpWithin(totals.closest('[data-cart]')) || cart.items_subtotal_price;
      const saved = Math.max(0, mrp - cart.items_subtotal_price);

      const mrpRow = totals.querySelector('[data-mrp-row]');
      const savRow = totals.querySelector('[data-savings-row]');
      const mrpVal = totals.querySelector('[data-cart-mrp]');
      const savVal = totals.querySelector('[data-cart-savings]');
      if (mrpRow) mrpRow.hidden = false;
      if (savRow) savRow.hidden = false;
      if (mrpVal) mrpVal.innerHTML = priceHtml(mrp);
      if (savVal) savVal.innerHTML = `&minus;${priceHtml(saved)}`;

      const gcRow = totals.querySelector('[data-giftcard-row]');
      if (gcRow) {
        gcRow.hidden = !(applied && off > 0);
        const tag = gcRow.querySelector('[data-giftcard-tag]');
        const amt = gcRow.querySelector('[data-giftcard-amount]');
        if (tag) tag.textContent = applied;
        if (amt) amt.innerHTML = `&minus;${priceHtml(off)}`;
      }
    });

    /* "Got a gift card?" in the bag, or the green "<CODE> applied" row with
       its Remove once one is on it. */
    all('[data-giftcard-line]').forEach((line) => {
      line.classList.toggle('is-applied', Boolean(applied));
      const codeEl = line.querySelector('[data-giftcard-line-code]');
      if (codeEl) codeEl.textContent = applied ? t('code_applied', '__CODE__ applied').replace('__CODE__', applied) : '';
    });

    all('[data-giftcard]').forEach((box) => {
      const input = box.querySelector('[data-giftcard-input]');
      const applyBtn = box.querySelector('[data-giftcard-apply]');
      const removeBtn = box.querySelector('[data-giftcard-remove]');
      const msg = box.querySelector('[data-giftcard-msg]');

      box.classList.toggle('is-applied', Boolean(applied));
      if (input) {
        input.value = applied || '';
        input.readOnly = Boolean(applied);
      }
      if (applyBtn) applyBtn.hidden = Boolean(applied);
      if (removeBtn) removeBtn.hidden = !applied;
      // "Gift card applied" is a confirmation, not a label. It says its piece
      // and goes, two seconds later, leaving the tick and the code to carry the
      // state. Keyed on the code so a re-render from any other cause does not
      // bring it back: render() runs on every quantity change.
      if (msg && applied) {
        if (box.dataset.said !== applied) {
          box.dataset.said = applied;
          msg.textContent = t('applied', 'Gift card applied');
          msg.hidden = false;
          msg.classList.remove('is-error');
          clearTimeout(Number(box.dataset.sayTimer) || 0);
          box.dataset.sayTimer = String(setTimeout(function () {
            if (!msg.classList.contains('is-error')) msg.hidden = true;
          }, 2000));
        }
      } else if (msg && !msg.classList.contains('is-error')) {
        delete box.dataset.said;
        msg.hidden = true;
      }
    });
  }

  /* ---- The sheet --------------------------------------------------------
     "Got a gift card?" opens its surface's sheet (snippets/v-gift-card.liquid)
     with a second showModal(), which stacks it above the bag in the top layer.
     It leaves by sliding back down; Escape and a tap on the backdrop do the
     same. */
  const stillMotion = () =>
    Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function openSheet(sheet) {
    if (!sheet || sheet.open || typeof sheet.showModal !== 'function') return;
    sheet.showModal();
    /* Straight into the field, inside the tap, so a phone raises its keyboard.
       With a code already on the bag there is nothing to type. */
    const input = sheet.querySelector('[data-giftcard-input]');
    if (input && !input.readOnly) input.focus();
  }

  function closeSheet(sheet) {
    if (!sheet || !sheet.open || sheet.dataset.closing) return Promise.resolve();
    sheet.dataset.closing = '1';
    const done = () => {
      delete sheet.dataset.closing;
      if (sheet.open) sheet.close();
    };
    if (stillMotion() || typeof sheet.animate !== 'function') {
      done();
      return Promise.resolve();
    }
    const slide = sheet.animate(
      [{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }],
      { duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
    );
    return slide.finished.catch(() => {}).then(() => { done(); slide.cancel(); });
  }

  /* ---- The applied moment ------------------------------------------------
     Modelled frame by frame on what Magic Checkout does when a coupon lands
     (screen recording, 2026-10-01): a green seal, a "<CODE> applied" chip,
     the total and a "Saved" pill; confetti bursts while the total rolls digit
     by digit from the old figure to the new one; it holds for a beat, then
     leaves. Since 2026-10-05 it plays inside the gift card sheet, not over
     the bag (Aditya): the field fades, the sheet grows to fit the moment,
     and when it is over the sheet slides away. The bag underneath already
     shows the discount row, because render() ran first. Styles sit in the
     stylesheet block of snippets/v-gift-card.liquid. */
  const CONFETTI = ['#2F6BFF', '#FFC531', '#F2385A', '#8E5BFF', '#1EC26A', '#FF7A1A'];
  const SEAL =
    '<svg class="v-gcx__seal" viewBox="0 0 48 48" aria-hidden="true">' +
      '<path fill="currentColor" d="M24 2l4.6 3.6 5.8-.8 2.3 5.4 5.4 2.3-.8 5.8L45 24l-3.7 4.6.8 5.8-5.4 2.3-2.3 5.4-5.8-.8L24 46l-4.6-3.7-5.8.8-2.3-5.4-5.4-2.3.8-5.8L3 24l3.7-4.6-.8-5.8 5.4-2.3 2.3-5.4 5.8.8z"/>' +
      '<path d="M15.5 24.5l6 6 11-12" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  const TICK =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
  /* One column per character. Digits get a 0-9 strip that scrolls; the rupee
     sign and the separators stay put. */
  const rollerHtml = (text) => Array.from(text).map((ch) => (
    /\d/.test(ch)
      ? '<span class="v-gcx__col"><span class="v-gcx__strip">' +
          '0123456789'.split('').map((d) => '<i>' + d + '</i>').join('') +
        '</span></span>'
      : '<span class="v-gcx__chr">' + esc(ch) + '</span>'
  )).join('');
  const digitsOf = (text) => Array.from(text).filter((c) => /\d/.test(c)).map(Number);

  /* Park every strip on the digit of the OLD total, paired from the right so
     ₹2,499 -> ₹1,250 lines the units up with the units. A column the old
     figure did not have starts a few steps away, so it visibly rolls too. */
  function park(totalEl, fromText, toText) {
    const to = digitsOf(toText);
    const from = digitsOf(fromText);
    const pad = from.length - to.length;
    totalEl.querySelectorAll('.v-gcx__col').forEach((col, i) => {
      const src = from[i + pad];
      const d = src === undefined ? (to[i] + 4) % 10 : src;
      const strip = col.querySelector('.v-gcx__strip');
      strip.style.transition = 'none';
      strip.style.transform = 'translateY(-' + d + 'em)';
    });
  }
  /* Then send each strip to the new digit, columns leaving 40ms apart. */
  function rollTo(totalEl, toText) {
    const to = digitsOf(toText);
    totalEl.querySelectorAll('.v-gcx__col').forEach((col, i) => {
      const strip = col.querySelector('.v-gcx__strip');
      strip.style.transition = '';
      strip.style.transitionDelay = (i * 40) + 'ms';
      strip.style.transform = 'translateY(-' + to[i] + 'em)';
    });
  }

  function confetti(canvas, ms) {
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return () => {};
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    const parts = [];
    for (let i = 0; i < 90; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = 5 + Math.random() * 9;
      parts.push({
        x: w / 2, y: h / 2 - 24,
        vx: Math.cos(a) * s, vy: Math.sin(a) * s - 3,
        r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
        w: 5 + Math.random() * 5, h: 3 + Math.random() * 4,
        c: CONFETTI[i % CONFETTI.length],
      });
    }
    const t0 = performance.now();
    let raf = 0;
    const tick = (now) => {
      const el = now - t0;
      ctx.clearRect(0, 0, w, h);
      const tail = ms * 0.55;
      ctx.globalAlpha = el < tail ? 1 : Math.max(0, 1 - (el - tail) / (ms - tail));
      parts.forEach((p) => {
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + 0.22;
        p.x += p.vx;
        p.y += p.vy;
        p.r += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (el < ms) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, w, h);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }

  function celebrate(box, code, fromCents, toCents, savedCents) {
    const sheet = box && box.closest('[data-giftcard-sheet]');
    const body = sheet && sheet.querySelector('[data-giftcard-sheet-body]');
    if (!sheet || !body || !sheet.open) return;
    const still = stillMotion();
    const fromText = money(fromCents);
    const toText = money(toCents);

    const el = document.createElement('div');
    el.className = 'v-gcx';
    el.setAttribute('role', 'status');
    el.innerHTML =
      '<div class="v-gcx__stack">' +
        SEAL +
        '<span class="v-gcx__chip">' + TICK + '<span>' +
          esc(t('code_applied', '__CODE__ applied').replace('__CODE__', code)) + '</span></span>' +
        '<span class="v-gcx__total" aria-label="' + esc(toText) + '">' + rollerHtml(toText) + '</span>' +
        '<span class="v-gcx__saved">' +
          esc(t('saved', 'Saved __AMOUNT__').replace('__AMOUNT__', money(savedCents))) + '</span>' +
      '</div>';
    const canvas = document.createElement('canvas');
    canvas.className = 'v-gcx__confetti';
    canvas.setAttribute('aria-hidden', 'true');

    const total = el.querySelector('.v-gcx__total');
    const timers = [];
    let stop = null;
    let gone = false;

    /* The field gives way to the moment, and the sheet grows from its own
       height to the moment's, so nothing jumps. */
    const swap = () => {
      const from = sheet.getBoundingClientRect().height;
      body.style.display = 'none';
      sheet.appendChild(el);
      sheet.appendChild(canvas);
      const to = sheet.getBoundingClientRect().height;
      if (!still && typeof sheet.animate === 'function' && Math.abs(to - from) > 1) {
        sheet.animate([{ height: from + 'px' }, { height: to + 'px' }],
          { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      }
    };
    /* The field comes back for next time, showing the code that is on. */
    const restore = () => {
      el.remove();
      canvas.remove();
      body.getAnimations && body.getAnimations().forEach((a) => a.cancel());
      body.style.display = '';
    };
    const end = () => {
      gone = true;
      timers.forEach(clearTimeout);
      if (stop) stop();
    };
    const leave = () => {
      if (gone) return;
      end();
      closeSheet(sheet).then(restore);
    };
    /* A tap ends it early; nobody is made to wait for confetti. */
    el.addEventListener('click', leave);
    /* Closed another way (Escape, the backdrop): put the field back. */
    sheet.addEventListener('close', () => {
      if (gone) return;
      end();
      restore();
    }, { once: true });

    if (still) {
      park(total, toText, toText);
      swap();
      el.classList.add('is-in');
      timers.push(setTimeout(leave, 1400));
      return;
    }
    park(total, fromText, toText);
    const fade = typeof body.animate === 'function'
      ? body.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out', fill: 'forwards' }).finished.catch(() => {})
      : Promise.resolve();
    fade.then(() => {
      if (gone) return;
      swap();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.classList.add('is-in');
        timers.push(setTimeout(() => {
          stop = confetti(canvas, 1500);
          rollTo(total, toText);
        }, 320));
        timers.push(setTimeout(leave, 2900));
      }));
    });
  }

  function fail(box, message) {
    const msg = box.querySelector('[data-giftcard-msg]');
    if (!msg) return;
    msg.textContent = message;
    msg.classList.add('is-error');
    msg.hidden = false;
  }

  function busy(box, on) {
    box.classList.toggle('is-busy', on);
    box.querySelectorAll('button').forEach((b) => { b.disabled = on; });
  }

  /* Shopify's Ajax cart applies a code directly: POST /cart/update.js with
     {"discount": "<code>"} answers with the whole cart, discount_codes[]
     .applicable included, in one round trip (0.8s measured). The old route,
     GET /discount/<code>, its redirect to the full /cart page and a /cart.js
     read after that, was three hops and most of the "it took three seconds"
     report. The old route stays as the fallback for a response that does not
     carry discount_codes at all. */
  async function apply(box) {
    const input = box.querySelector('[data-giftcard-input]');
    const code = (input && input.value || '').trim().toUpperCase();
    if (input) input.value = code;
    const msg = box.querySelector('[data-giftcard-msg]');
    if (msg) { msg.classList.remove('is-error'); msg.hidden = true; }
    box.classList.remove('is-error');
    if (!code) return;
    busy(box, true);
    try {
      let cart = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: code }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))));
      if (!Array.isArray(cart.discount_codes)) {
        await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, { credentials: 'same-origin' });
        cart = await getCart();
      }
      const applied = appliedCode(cart);
      render(cart);
      if (!applied) {
        box.classList.add('is-error');
        /* render() cleared the field; put the attempt back so it can be fixed. */
        if (input) input.value = code;
        fail(box, t('invalid', 'That code is not valid.'));
      } else if (cart.total_discount > 0) {
        /* The roll starts from what the bag cost before the code: the items
           subtotal, which is also what the sheet shows while nothing is
           applied. The keyboard goes down first so the moment is seen. */
        if (input) input.blur();
        celebrate(box, applied, cart.items_subtotal_price, cart.total_price, cart.total_discount);
      }
    } catch (e) {
      box.classList.add('is-error');
      fail(box, t('error', 'Could not apply that code. Try again.'));
    } finally {
      busy(box, false);
    }
  }

  const clearDiscount = () => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ discount: '' }),
  });

  async function remove(box) {
    busy(box, true);
    const msg = box.querySelector('[data-giftcard-msg]');
    if (msg) { msg.classList.remove('is-error'); msg.hidden = true; }
    try {
      await clearDiscount();
      render(await getCart());
    } catch (e) {
      fail(box, t('error', 'Could not remove that code. Try again.'));
    } finally {
      busy(box, false);
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-totals-toggle]');
    if (!btn) return;
    const totals = btn.closest('[data-totals]');
    const rows = totals && totals.querySelector('[data-totals-rows]');
    if (!rows) return;
    const open = rows.hidden;
    rows.hidden = !open;
    totals.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', String(open));
    btn.setAttribute('aria-label', open ? t('hide', 'Hide charge breakdown') : t('show', 'Show charge breakdown'));
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-giftcard-open]');
    if (opener) {
      e.preventDefault();
      openSheet(document.getElementById(opener.getAttribute('data-giftcard-open')));
      return;
    }
    const closer = e.target.closest('[data-giftcard-close]');
    if (closer) {
      e.preventDefault();
      closeSheet(closer.closest('[data-giftcard-sheet]'));
      return;
    }
    /* A tap on the backdrop lands on the <dialog> itself. */
    if (e.target.matches && e.target.matches('[data-giftcard-sheet]')) closeSheet(e.target);
  });
  /* Escape slides it away too, instead of snapping it shut. `cancel` does not
     bubble, so this listens on the way down. */
  document.addEventListener('cancel', (e) => {
    if (!e.target.matches || !e.target.matches('[data-giftcard-sheet]')) return;
    e.preventDefault();
    closeSheet(e.target);
  }, true);

  /* While the sheet is up, Escape is the sheet's alone. The bag's own key
     handler (assets/drawer-cartfix-*.js) closes the bag on any Escape, so one
     key would take down both. Stopping it on the way in leaves the browser's
     default action alone, and that default is what fires `cancel` above. */
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !document.querySelector('[data-giftcard-sheet][open]')) return;
    e.stopImmediatePropagation();
  }, true);

  /* Typing again is the way out of the error state. */
  document.addEventListener('input', (e) => {
    const box = e.target.closest('[data-giftcard]');
    if (!box || !box.classList.contains('is-error')) return;
    box.classList.remove('is-error');
    const msg = box.querySelector('[data-giftcard-msg]');
    if (msg) { msg.classList.remove('is-error'); msg.hidden = true; }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-giftcard-form]');
    if (!form) return;
    e.preventDefault();
    apply(form.closest('[data-giftcard]'));
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-giftcard-remove]');
    if (!btn) return;
    e.preventDefault();
    remove(btn.closest('[data-giftcard]'));
  });

  /* Remove on the bag's applied row: takes the code off where it sits and
     the row goes back to "Got a gift card?". Focus moves to that prompt, so
     a keyboard is not left on a button that has just disappeared. */
  async function removeFromLine(line) {
    if (line.classList.contains('is-busy')) return;
    const hadFocus = line.contains(document.activeElement);
    line.classList.add('is-busy');
    try {
      await clearDiscount();
      render(await getCart());
    } catch (e) {
      getCart().then(render).catch(() => {});
    } finally {
      line.classList.remove('is-busy');
      const open = line.querySelector('[data-giftcard-open]');
      if (hadFocus && open && !line.classList.contains('is-applied')) open.focus({ preventScroll: true });
    }
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-giftcard-line-remove]');
    if (!btn) return;
    e.preventDefault();
    removeFromLine(btn.closest('[data-giftcard-line]'));
  });

  // cart.js owns the subtotal. When it rewrites one, the totals below it are
  // stale, so refetch. Debounced because cart.js writes twice per change:
  // once optimistically, once from the server response.
  let pending;
  const refresh = () => {
    clearTimeout(pending);
    pending = setTimeout(() => { getCart().then(render).catch(() => {}); }, 180);
  };
  all('[data-cart-subtotal]').forEach((el) => {
    new MutationObserver(refresh).observe(el, { childList: true, subtree: true, characterData: true });
  });

  getCart().then(render).catch(() => {});
})();
